import React, { useState, useEffect } from 'react';
import { useLocation, Link as RouterLink } from 'react-router-dom';
import {
  Container,
  Box,
  Typography,
  Grid,
  Paper,
  Breadcrumbs,
  Link,
  CircularProgress,
  Alert,
  Divider,
  Chip
} from '@mui/material';
import {
  Home as HomeIcon,
  Search as SearchIcon
} from '@mui/icons-material';
import { searchInventoryItems } from '../../services/inventoryService';
import { InventoryItem } from '../../utils/types';
import ProductCard from '../../components/store/ProductCard';

const SearchResultsPage: React.FC = () => {
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const query = searchParams.get('q') || '';

  const [products, setProducts] = useState<InventoryItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchSearchResults = async () => {
      if (!query) {
        setProducts([]);
        setLoading(false);
        return;
      }

      setLoading(true);
      setError(null);

      try {
        const allResults = await searchInventoryItems(query);
        const storeProducts = allResults.filter(item => item.showInStore !== false);
        setProducts(storeProducts);
      } catch (err) {
        console.error('Error searching products:', err);
        setError('حدث خطأ أثناء البحث عن المنتجات. يرجى المحاولة مرة أخرى.');
      } finally {
        setLoading(false);
      }
    };

    fetchSearchResults();
  }, [query]);

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 8 }}>
      {/* فتات الخبز */}
      <Breadcrumbs sx={{ mb: 2 }}>
        <Link component={RouterLink} to="/" color="inherit" sx={{ display: 'flex', alignItems: 'center' }}>
          <HomeIcon sx={{ mr: 0.5 }} fontSize="inherit" />
        </Link>
        <Typography color="text.primary" sx={{ display: 'flex', alignItems: 'center' }}>
          <SearchIcon sx={{ mr: 0.5 }} fontSize="inherit" />
          نتائج البحث
        </Typography>
      </Breadcrumbs>

      {/* عنوان الصفحة */}
      <Box sx={{ mb: 4 }}>
        <Typography variant="h4" component="h1" gutterBottom>
          نتائج البحث
        </Typography>
        <Chip
          label={`"${query}"`}
          color="primary"
          variant="outlined"
          sx={{ mr: 1, mb: 1 }}
        />
      </Box>

      {/* محتوى البحث */}
      <Paper elevation={1} sx={{ p: 3, mb: 4 }}>
        {loading ? (
          <Box display="flex" justifyContent="center" p={4}>
            <CircularProgress />
          </Box>
        ) : error ? (
          <Alert severity="error" sx={{ mb: 3 }}>{error}</Alert>
        ) : products.length === 0 ? (
          <Alert severity="info" sx={{ mb: 3 }}>
            لا توجد منتجات مطابقة لـ "{query}"
          </Alert>
        ) : (
          <>
            <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Typography variant="h6" component="h2">
                تم العثور على {products.length} منتج
              </Typography>
            </Box>
            <Divider sx={{ mb: 3 }} />
            <Grid container spacing={{ xs: 1.5, sm: 2, md: 3 }}>
              {products.map((product) => (
                <Grid item xs={6} sm={6} md={4} lg={3} key={product.id}>
                  <ProductCard product={product} />
                </Grid>
              ))}
            </Grid>
          </>
        )}
      </Paper>
    </Container>
  );
};

export default SearchResultsPage;
